// ============================================================================
// Verifactu — UI strings (es / en / ca), registered straight into i18next on
// import (same pattern as locales/documentMail.js) so the big JSON files are
// not touched. Also carries the 'Void' (Anulada) document status, the new
// Historial rows and the rectificativa cause / régimen labels.
// ============================================================================
import i18n from '../lib/i18n';

const es = {
  tb: {
    settingsTitle: 'TicketBAI (País Vasco)',
    intro: 'Para empresas y autónomos de Araba, Bizkaia o Gipuzkoa: cada factura y ticket genera un fichero TicketBAI firmado con tu certificado y encadenado al anterior, el documento lleva el identificativo TBAI y su código QR, y el fichero se envía a tu Hacienda Foral (Bizkaia lo recibe dentro del LROE – Batuz).',
    needsMigration: 'TicketBAI se activa al aplicar backend/ticketbai.sql en la base de datos.',
    territory: 'Territorio',
    territoryHint: 'La Hacienda Foral que recibe tus ficheros. Se usa el certificado digital guardado en el apartado Verifactu.',
    territory_none: '— Elige —',
    territory_araba: 'Araba/Álava',
    territory_bizkaia: 'Bizkaia (Batuz · LROE)',
    territory_gipuzkoa: 'Gipuzkoa',
    modeTitle: 'Estado',
    mode_off: 'Desactivado',
    mode_offHint: 'Las facturas se emiten como hasta ahora, sin fichero TicketBAI.',
    mode_test: 'Pruebas (entorno de pruebas de la Hacienda Foral)',
    mode_testHint: 'Los ficheros van al entorno de pruebas y no tienen validez tributaria. Úsalo primero.',
    mode_production: 'Producción',
    mode_productionHint: 'Cada factura y ticket emitido se envía a la Hacienda Foral. Los documentos llevan el identificativo TBAI y el QR.',
    license: 'Licencia TicketBAI (pruebas)',
    licenseHint: 'Código temporal que la Hacienda Foral facilita para su entorno de pruebas (Araba y Gipuzkoa lo envían por correo). En producción se usa la licencia del software registrada en el servidor.',
    licenseServer: 'Licencia de producción configurada en el servidor para {{territory}}.',
    licenseMissing: 'Aún no hay licencia de producción para {{territory}} en el servidor (TBAI_LICENSE_…); sólo el modo Pruebas está disponible.',
    bizkaiaTest: 'En pruebas, Bizkaia acepta el software de pruebas publicado por Batuz sin licencia propia.',
    epigrafe: 'Epígrafe IAE',
    epigrafeHint: 'Obligatorio para autónomos en Bizkaia: el LROE (modelo 140) lo pide en cada ingreso.',
    device: 'Nº de serie del dispositivo (opcional)',
    foreign: 'Ventas a clientes extranjeros',
    foreign_entrega: 'Entregas de bienes',
    foreign_servicios: 'Prestaciones de servicios',
    save: 'Guardar',
    saved: 'Configuración guardada.',
    testSubmission: 'Enviar factura de prueba',
    testSubmissionHint: 'Firma y envía un fichero de ejemplo al entorno de pruebas del territorio. No se guarda ni afecta a tus facturas.',
    testSubmissionOk: 'La Hacienda Foral ha aceptado la prueba {{number}}{{csv}}.',
    testSubmissionFail: 'La Hacienda Foral no ha aceptado la prueba: {{message}}',
    counts: 'Ficheros: {{total}} · aceptados {{accepted}} · pendientes {{pending}} · rechazados {{rejected}}',
    noCert: 'Sube primero tu certificado digital en el apartado Verifactu de esta página.',
    softwareNifMissing: 'Producción requiere el NIF de la entidad desarrolladora en el servidor (TBAI_DEV_NIF).',
    ownerOnly: 'Sólo el titular de la cuenta puede cambiar esta configuración.',
    error: 'Error: {{message}}',
    panelTitle: 'TicketBAI',
    status_pending: 'Pendiente de envío a la Hacienda Foral',
    status_accepted: 'Recibido por la Hacienda Foral',
    status_accepted_with_errors: 'Aceptado por la Hacienda Foral con errores',
    status_rejected: 'Rechazado por la Hacienda Foral',
    status_error: 'No se pudo enviar a la Hacienda Foral',
    status_voided: 'Anulación recibida por la Hacienda Foral',
    status_none: 'Sin fichero TicketBAI',
    csv: 'Justificante',
    huella: 'Identificativo TBAI',
    environment_test: 'Entorno de pruebas',
    environment_production: 'Producción',
    register: 'Generar y enviar fichero TicketBAI',
    verifyLink: 'Comprobar en la Hacienda Foral (QR)',
    registeredOk: 'Enviado a la Hacienda Foral.',
  },
  vf: {
    title: 'Verifactu',
    settingsTitle: 'Verifactu (AEAT)',
    intro: 'Cada factura y ticket que emites genera un registro de facturación con huella encadenada y código QR, y se envía a la Agencia Tributaria con tu certificado digital. Obligatorio para el software de facturación (RD 1007/2023).',
    certTitle: 'Certificado digital',
    certHint: 'Certificado de representante o de persona física (FNMT, Camerfirma…) en formato .p12 / .pfx. Se guarda cifrado y sólo lo usa el servidor para firmar la conexión con la AEAT.',
    certFile: 'Archivo .p12 / .pfx',
    certPassword: 'Contraseña del certificado',
    certUpload: 'Guardar certificado',
    certUploading: 'Comprobando…',
    certStored: 'Certificado guardado',
    certHolder: 'Titular',
    certNif: 'NIF del certificado',
    certValidTo: 'Válido hasta',
    certDaysLeft: '{{days}} días',
    certExpired: 'Caducado',
    certReplace: 'Sustituir certificado',
    certDelete: 'Quitar certificado',
    certDeleteConfirm: '¿Quitar el certificado? Verifactu quedará desactivado hasta que subas otro.',
    certNifMismatch: 'El NIF del certificado ({{cert}}) no coincide con el NIF de tu empresa ({{issuer}}). La AEAT sólo acepta registros del titular o de su representante.',
    certOk: 'Certificado guardado: {{subject}}',
    testCert: 'Probar conexión con la AEAT',
    testCertOk: 'Conexión correcta con {{host}} ({{protocol}}).',
    testCertOkNif: 'La AEAT reconoce el certificado y lo acepta para el NIF {{nif}} ({{host}}).',
    testCertFail: 'La AEAT no ha aceptado la conexión: {{message}}',
    testSubmission: 'Enviar factura de prueba (pre-producción)',
    testSubmissionHint: 'Envía un registro de ejemplo al entorno de pruebas de la AEAT. No se guarda ni afecta a tus facturas.',
    testSubmissionOk: 'La AEAT ha aceptado la factura de prueba {{number}}{{csv}}.',
    testSubmissionFail: 'La AEAT no ha aceptado la prueba: {{message}}',
    modeTitle: 'Estado',
    mode_off: 'Desactivado',
    mode_offHint: 'Las facturas se emiten como hasta ahora, sin registro ni QR.',
    mode_test: 'Pruebas (pre-producción AEAT)',
    mode_testHint: 'Los registros se envían al entorno de pruebas de la AEAT. Sirve para comprobar tu certificado y tus datos sin efectos fiscales.',
    mode_production: 'Producción',
    mode_productionHint: 'Cada factura y ticket emitido se registra en la AEAT. Los documentos llevan el QR tributario y la leyenda VERI*FACTU.',
    modeSaved: 'Configuración guardada.',
    softwareNifMissing: 'El servidor aún no tiene configurado el NIF del productor del software (VERIFACTU_DEV_NIF); el modo Producción se activará cuando lo esté.',
    counts: 'Registros: {{total}} · aceptados {{accepted}} · pendientes {{pending}} · rechazados {{rejected}}',
    since: 'Activo desde {{date}}',
    needsMigration: 'Verifactu se activa al aplicar backend/verifactu.sql en la base de datos.',
    ownerOnly: 'Sólo el titular de la cuenta puede cambiar esta configuración.',
    error: 'Verifactu: {{message}}',
    // Invoice view panel
    panelTitle: 'Verifactu',
    status_pending: 'Pendiente de envío a la AEAT',
    status_accepted: 'Registrado en la AEAT',
    status_accepted_with_errors: 'Registrado en la AEAT con avisos',
    status_rejected: 'Rechazado por la AEAT',
    status_error: 'No se pudo enviar a la AEAT',
    status_voided: 'Anulación registrada en la AEAT',
    status_none: 'Sin registro Verifactu',
    csv: 'CSV',
    huella: 'Huella',
    environment_test: 'Entorno de pruebas',
    environment_production: 'Producción',
    retry: 'Reintentar envío',
    resend: 'Corregir y reenviar',
    retrying: 'Enviando…',
    register: 'Registrar en la AEAT',
    verifyLink: 'Comprobar en la sede de la AEAT',
    records: 'Registros ({{count}})',
    record_alta: 'Alta',
    record_anulacion: 'Anulación',
    downloadXml: 'Descargar XML',
    errorDetail: 'Error {{code}}: {{description}}',
    registeredOk: 'Registro Verifactu enviado a la AEAT.',
    registerWarning: 'La factura se ha emitido, pero el registro Verifactu no se ha podido enviar todavía: {{message}} Podrás reintentarlo desde la factura.',
    exemptionRequired: 'Con Verifactu activo, las líneas al 0 % necesitan un motivo de exención (Opciones avanzadas).',
    // Void
    void: 'Anular factura',
    voidHint: 'Anula una factura emitida por error. Deja de contar en totales e informes y, si estaba registrada, se envía la anulación a la AEAT. Si el cliente ya la tiene, emite una rectificativa en su lugar.',
    voidPrompt: 'Motivo de la anulación (obligatorio):',
    voidConfirm: '¿Anular la factura {{number}}? Esta acción no se puede deshacer.',
    voiding: 'Anulando…',
    voided: 'Anulada',
    voidedOn: 'Anulada el {{date}}',
    voidReason: 'Motivo: {{reason}}',
    voidError: 'No se pudo anular: {{message}}',
    // Régimen
    regimeLabel: 'Clave de régimen (Verifactu)',
    regimeHint: 'Régimen de IVA de la operación. Régimen general (01) para casi todo; 18 se aplica solo con recargo de equivalencia.',
    regimeDefault: 'General (01)',
    regime_01: 'Operación de régimen general',
    regime_02: 'Exportación',
    regime_03: 'Bienes usados, arte, antigüedades (REBU)',
    regime_04: 'Oro de inversión',
    regime_05: 'Agencias de viajes',
    regime_06: 'Grupo de entidades (nivel avanzado)',
    regime_07: 'Criterio de caja',
    regime_08: 'Operaciones sujetas a IPSI / IGIC',
    regime_09: 'Agencias de viajes que median en nombre ajeno',
    regime_10: 'Cobros por cuenta de terceros',
    regime_11: 'Arrendamiento de local de negocio',
    regime_14: 'Factura con IVA pendiente de devengo (obra pública)',
    regime_15: 'Factura con IVA pendiente de devengo (tracto sucesivo)',
    regime_17: 'Ventanilla única (OSS / IOSS)',
    regime_18: 'Recargo de equivalencia',
    regime_19: 'Actividades REAGYP',
    regime_20: 'Régimen simplificado',
    // Rectificativa causes
    causeTitle: 'Motivo de la rectificativa',
    causeHint: 'La factura rectificativa debe indicar por qué se emite (art. 80 LIVA). Se registra en la AEAT con este código.',
    cause_R1: 'R1 · Error fundado en derecho y art. 80.Uno, Dos y Seis LIVA',
    cause_R2: 'R2 · Concurso de acreedores (art. 80.Tres)',
    cause_R3: 'R3 · Deudas incobrables (art. 80.Cuatro)',
    cause_R4: 'R4 · Resto de causas',
    cause_R5: 'R5 · Rectificativa de facturas simplificadas',
    typeTitle: 'Tipo',
    type_I: 'Por diferencias (la rectificativa recoge el importe que se corrige)',
    type_S: 'Por sustitución (sustituye íntegramente a la factura original)',
    creditNoteTitle: 'Emitir factura rectificativa',
    creditNoteBody: 'Se creará una rectificativa negativa por el importe total de {{number}} ({{amount}}) y la factura quedará anulada por ella.',
    confirm: 'Emitir rectificativa',
    cancel: 'Cancelar',
  },
  docstatus: { Void: 'Anulada' },
  docHistory: {
    verifactu_pending: 'Registro Verifactu ({{kind}}) generado, pendiente de envío',
    verifactu_accepted: 'Registro Verifactu ({{kind}}) aceptado por la AEAT{{csv}}',
    verifactu_accepted_with_errors: 'Registro Verifactu ({{kind}}) aceptado con avisos{{csv}}',
    verifactu_rejected: 'Registro Verifactu ({{kind}}) rechazado por la AEAT: {{error}}',
    verifactu_error: 'Registro Verifactu ({{kind}}) no enviado: {{error}}',
    verifactu_kind_alta: 'alta',
    verifactu_kind_anulacion: 'anulación',
    voided: 'Factura anulada: {{reason}}',
  },
  doctpl: {
    vfLegend: 'VERI*FACTU',
    tbaiLegend: 'TicketBAI',
    vfScan: 'Factura verificable en la sede electrónica de la AEAT',
    vfVoid: 'ANULADA',
  },
  subdash: { f: { verifactuAeat: 'Verifactu: registros con huella, QR y envío a la AEAT.' } },
};

const en = {
  tb: {
    settingsTitle: 'TicketBAI (Basque Country)',
    intro: 'For businesses in Araba, Bizkaia or Gipuzkoa: every issued invoice and ticket becomes a TicketBAI file signed with your certificate and chained to the previous one, the document carries the TBAI identifier and its QR, and the file is sent to your Hacienda Foral (Bizkaia receives it inside the LROE – Batuz).',
    needsMigration: 'TicketBAI becomes available once backend/ticketbai.sql is applied to the database.',
    territory: 'Territory',
    territoryHint: 'The Hacienda Foral that receives your files. The digital certificate stored in the Verifactu card is used.',
    territory_none: '— Choose —',
    territory_araba: 'Araba/Álava',
    territory_bizkaia: 'Bizkaia (Batuz · LROE)',
    territory_gipuzkoa: 'Gipuzkoa',
    modeTitle: 'Status',
    mode_off: 'Off',
    mode_offHint: 'Invoices are issued as before, without a TicketBAI file.',
    mode_test: 'Testing (the Hacienda Foral test environment)',
    mode_testHint: 'Files go to the test environment and have no tax effect. Use it first.',
    mode_production: 'Production',
    mode_productionHint: 'Every issued invoice and ticket is sent to the Hacienda Foral. Documents carry the TBAI identifier and the QR.',
    license: 'TicketBAI licence (testing)',
    licenseHint: 'Temporary code the Hacienda Foral hands out for its test environment (Araba and Gipuzkoa send it by e-mail). Production uses the software licence configured on the server.',
    licenseServer: 'Production licence configured on the server for {{territory}}.',
    licenseMissing: 'No production licence for {{territory}} on the server yet (TBAI_LICENSE_…); only Testing is available.',
    bizkaiaTest: 'In testing, Bizkaia accepts the test software published by Batuz without a licence of your own.',
    epigrafe: 'IAE heading (epígrafe)',
    epigrafeHint: 'Required for self-employed people in Bizkaia: the LROE (model 140) asks for it on every income.',
    device: 'Device serial number (optional)',
    foreign: 'Sales to foreign clients',
    foreign_entrega: 'Supplies of goods',
    foreign_servicios: 'Supplies of services',
    save: 'Save',
    saved: 'Settings saved.',
    testSubmission: 'Send a test invoice',
    testSubmissionHint: 'Signs and sends a sample file to the territory\'s test environment. Nothing is stored.',
    testSubmissionOk: 'The Hacienda Foral accepted the test {{number}}{{csv}}.',
    testSubmissionFail: 'The Hacienda Foral did not accept the test: {{message}}',
    counts: 'Files: {{total}} · accepted {{accepted}} · pending {{pending}} · rejected {{rejected}}',
    noCert: 'Upload your digital certificate in the Verifactu card on this page first.',
    softwareNifMissing: 'Production requires the software developer\'s NIF on the server (TBAI_DEV_NIF).',
    ownerOnly: 'Only the account owner can change these settings.',
    error: 'Error: {{message}}',
    panelTitle: 'TicketBAI',
    status_pending: 'Waiting to be sent to the Hacienda Foral',
    status_accepted: 'Received by the Hacienda Foral',
    status_accepted_with_errors: 'Accepted by the Hacienda Foral with errors',
    status_rejected: 'Rejected by the Hacienda Foral',
    status_error: 'Could not be sent to the Hacienda Foral',
    status_voided: 'Cancellation received by the Hacienda Foral',
    status_none: 'No TicketBAI file',
    csv: 'Receipt',
    huella: 'TBAI identifier',
    environment_test: 'Test environment',
    environment_production: 'Production',
    register: 'Generate and send the TicketBAI file',
    verifyLink: 'Check with the Hacienda Foral (QR)',
    registeredOk: 'Sent to the Hacienda Foral.',
  },
  vf: {
    title: 'Verifactu',
    settingsTitle: 'Verifactu (Spanish tax agency)',
    intro: 'Every invoice and ticket you issue generates a chained billing record with a fingerprint and QR code, sent to the Spanish tax agency (AEAT) with your digital certificate. Mandatory for invoicing software (RD 1007/2023).',
    certTitle: 'Digital certificate',
    certHint: 'A representative or personal certificate (FNMT, Camerfirma…) as .p12 / .pfx. Stored encrypted; only the server uses it to sign the connection with the AEAT.',
    certFile: '.p12 / .pfx file',
    certPassword: 'Certificate password',
    certUpload: 'Save certificate',
    certUploading: 'Checking…',
    certStored: 'Certificate stored',
    certHolder: 'Holder',
    certNif: 'Certificate tax ID',
    certValidTo: 'Valid until',
    certDaysLeft: '{{days}} days',
    certExpired: 'Expired',
    certReplace: 'Replace certificate',
    certDelete: 'Remove certificate',
    certDeleteConfirm: 'Remove the certificate? Verifactu stays off until you upload another one.',
    certNifMismatch: 'The certificate tax ID ({{cert}}) differs from your company tax ID ({{issuer}}). The AEAT only accepts records from the holder or their representative.',
    certOk: 'Certificate stored: {{subject}}',
    testCert: 'Test the AEAT connection',
    testCertOk: 'Connected to {{host}} ({{protocol}}).',
    testCertOkNif: 'The AEAT recognises the certificate and accepts it for NIF {{nif}} ({{host}}).',
    testCertFail: 'The AEAT did not accept the connection: {{message}}',
    testSubmission: 'Send a test invoice (sandbox)',
    testSubmissionHint: 'Sends a sample record to the AEAT test environment. Nothing is stored and your invoices are untouched.',
    testSubmissionOk: 'The AEAT accepted test invoice {{number}}{{csv}}.',
    testSubmissionFail: 'The AEAT did not accept the test: {{message}}',
    modeTitle: 'Status',
    mode_off: 'Off',
    mode_offHint: 'Invoices are issued as before, with no record or QR.',
    mode_test: 'Testing (AEAT sandbox)',
    mode_testHint: 'Records go to the AEAT test environment. Use it to check your certificate and data with no tax effect.',
    mode_production: 'Production',
    mode_productionHint: 'Every issued invoice and ticket is registered with the AEAT. Documents carry the tax QR and the VERI*FACTU legend.',
    modeSaved: 'Settings saved.',
    softwareNifMissing: 'The server has no software-producer tax ID configured yet (VERIFACTU_DEV_NIF); Production becomes available once it is set.',
    counts: 'Records: {{total}} · accepted {{accepted}} · pending {{pending}} · rejected {{rejected}}',
    since: 'Active since {{date}}',
    needsMigration: 'Verifactu becomes available once backend/verifactu.sql is applied to the database.',
    ownerOnly: 'Only the account owner can change these settings.',
    error: 'Verifactu: {{message}}',
    panelTitle: 'Verifactu',
    status_pending: 'Waiting to be sent to the AEAT',
    status_accepted: 'Registered with the AEAT',
    status_accepted_with_errors: 'Registered with the AEAT with warnings',
    status_rejected: 'Rejected by the AEAT',
    status_error: 'Could not be sent to the AEAT',
    status_voided: 'Cancellation registered with the AEAT',
    status_none: 'No Verifactu record',
    csv: 'CSV',
    huella: 'Fingerprint',
    environment_test: 'Test environment',
    environment_production: 'Production',
    retry: 'Retry sending',
    resend: 'Fix and resend',
    retrying: 'Sending…',
    register: 'Register with the AEAT',
    verifyLink: 'Check on the AEAT site',
    records: 'Records ({{count}})',
    record_alta: 'Registration',
    record_anulacion: 'Cancellation',
    downloadXml: 'Download XML',
    errorDetail: 'Error {{code}}: {{description}}',
    registeredOk: 'Verifactu record sent to the AEAT.',
    registerWarning: 'The invoice was issued, but its Verifactu record could not be sent yet: {{message}} You can retry from the invoice.',
    exemptionRequired: 'With Verifactu on, 0 % lines need an exemption cause (Advanced options).',
    void: 'Void invoice',
    voidHint: 'Voids an invoice issued by mistake. It stops counting in totals and reports and, if registered, the cancellation is sent to the AEAT. If the customer already has it, issue a credit note instead.',
    voidPrompt: 'Reason for voiding (required):',
    voidConfirm: 'Void invoice {{number}}? This cannot be undone.',
    voiding: 'Voiding…',
    voided: 'Void',
    voidedOn: 'Voided on {{date}}',
    voidReason: 'Reason: {{reason}}',
    voidError: 'Could not void: {{message}}',
    regimeLabel: 'Regime key (Verifactu)',
    regimeHint: 'VAT regime of the operation. General (01) for almost everything; 18 only applies with the equivalence surcharge.',
    regimeDefault: 'General (01)',
    regime_01: 'General regime',
    regime_02: 'Export',
    regime_03: 'Second-hand goods, art, antiques',
    regime_04: 'Investment gold',
    regime_05: 'Travel agencies',
    regime_06: 'Group of entities (advanced)',
    regime_07: 'Cash-basis VAT',
    regime_08: 'Operations subject to IPSI / IGIC',
    regime_09: 'Travel agencies acting for others',
    regime_10: 'Collections on behalf of third parties',
    regime_11: 'Business premises rental',
    regime_14: 'VAT pending accrual (public works)',
    regime_15: 'VAT pending accrual (successive supplies)',
    regime_17: 'One-stop shop (OSS / IOSS)',
    regime_18: 'Equivalence surcharge',
    regime_19: 'Agriculture, livestock and fishing (REAGYP)',
    regime_20: 'Simplified regime',
    causeTitle: 'Reason for the credit note',
    causeHint: 'A corrective invoice must state why it is issued (art. 80 LIVA). It is registered with the AEAT under this code.',
    cause_R1: 'R1 · Legal error and art. 80.One, Two and Six LIVA',
    cause_R2: 'R2 · Insolvency proceedings (art. 80.Three)',
    cause_R3: 'R3 · Bad debts (art. 80.Four)',
    cause_R4: 'R4 · Other causes',
    cause_R5: 'R5 · Corrective of simplified invoices',
    typeTitle: 'Type',
    type_I: 'By differences (the credit note carries the corrected amount)',
    type_S: 'By substitution (fully replaces the original invoice)',
    creditNoteTitle: 'Issue credit note',
    creditNoteBody: 'A negative credit note for the full amount of {{number}} ({{amount}}) will be created and the invoice cancelled by it.',
    confirm: 'Issue credit note',
    cancel: 'Cancel',
  },
  docstatus: { Void: 'Void' },
  docHistory: {
    verifactu_pending: 'Verifactu record ({{kind}}) generated, waiting to be sent',
    verifactu_accepted: 'Verifactu record ({{kind}}) accepted by the AEAT{{csv}}',
    verifactu_accepted_with_errors: 'Verifactu record ({{kind}}) accepted with warnings{{csv}}',
    verifactu_rejected: 'Verifactu record ({{kind}}) rejected by the AEAT: {{error}}',
    verifactu_error: 'Verifactu record ({{kind}}) not sent: {{error}}',
    verifactu_kind_alta: 'registration',
    verifactu_kind_anulacion: 'cancellation',
    voided: 'Invoice voided: {{reason}}',
  },
  doctpl: {
    vfLegend: 'VERI*FACTU',
    tbaiLegend: 'TicketBAI',
    vfScan: 'Invoice verifiable at the AEAT electronic office',
    vfVoid: 'VOID',
  },
  subdash: { f: { verifactuAeat: 'Verifactu: fingerprinted records, QR and submission to the AEAT.' } },
};

const ca = {
  tb: {
    settingsTitle: 'TicketBAI (País Basc)',
    intro: 'Per a empreses i autònoms d\'Araba, Bizkaia o Gipuzkoa: cada factura i tiquet genera un fitxer TicketBAI signat amb el teu certificat i encadenat a l\'anterior, el document porta l\'identificatiu TBAI i el seu codi QR, i el fitxer s\'envia a la teva Hisenda Foral (Bizkaia el rep dins del LROE – Batuz).',
    needsMigration: 'TicketBAI s\'activa en aplicar backend/ticketbai.sql a la base de dades.',
    territory: 'Territori',
    territoryHint: 'La Hisenda Foral que rep els teus fitxers. S\'usa el certificat digital desat a l\'apartat Verifactu.',
    territory_none: '— Tria —',
    territory_araba: 'Araba/Àlaba',
    territory_bizkaia: 'Bizkaia (Batuz · LROE)',
    territory_gipuzkoa: 'Gipuzkoa',
    modeTitle: 'Estat',
    mode_off: 'Desactivat',
    mode_offHint: 'Les factures s\'emeten com fins ara, sense fitxer TicketBAI.',
    mode_test: 'Proves (entorn de proves de la Hisenda Foral)',
    mode_testHint: 'Els fitxers van a l\'entorn de proves i no tenen validesa tributària. Fes-lo servir primer.',
    mode_production: 'Producció',
    mode_productionHint: 'Cada factura i tiquet emès s\'envia a la Hisenda Foral. Els documents porten l\'identificatiu TBAI i el QR.',
    license: 'Llicència TicketBAI (proves)',
    licenseHint: 'Codi temporal que la Hisenda Foral facilita per al seu entorn de proves (Araba i Gipuzkoa l\'envien per correu). En producció s\'usa la llicència del programari registrada al servidor.',
    licenseServer: 'Llicència de producció configurada al servidor per a {{territory}}.',
    licenseMissing: 'Encara no hi ha llicència de producció per a {{territory}} al servidor (TBAI_LICENSE_…); només el mode Proves està disponible.',
    bizkaiaTest: 'En proves, Bizkaia accepta el programari de proves publicat per Batuz sense llicència pròpia.',
    epigrafe: 'Epígraf IAE',
    epigrafeHint: 'Obligatori per a autònoms a Bizkaia: el LROE (model 140) el demana a cada ingrés.',
    device: 'Núm. de sèrie del dispositiu (opcional)',
    foreign: 'Vendes a clients estrangers',
    foreign_entrega: 'Lliuraments de béns',
    foreign_servicios: 'Prestacions de serveis',
    save: 'Desa',
    saved: 'Configuració desada.',
    testSubmission: 'Envia una factura de prova',
    testSubmissionHint: 'Signa i envia un fitxer d\'exemple a l\'entorn de proves del territori. No es desa ni afecta les teves factures.',
    testSubmissionOk: 'La Hisenda Foral ha acceptat la prova {{number}}{{csv}}.',
    testSubmissionFail: 'La Hisenda Foral no ha acceptat la prova: {{message}}',
    counts: 'Fitxers: {{total}} · acceptats {{accepted}} · pendents {{pending}} · rebutjats {{rejected}}',
    noCert: 'Puja primer el teu certificat digital a l\'apartat Verifactu d\'aquesta pàgina.',
    softwareNifMissing: 'Producció requereix el NIF de l\'entitat desenvolupadora al servidor (TBAI_DEV_NIF).',
    ownerOnly: 'Només el titular del compte pot canviar aquesta configuració.',
    error: 'Error: {{message}}',
    panelTitle: 'TicketBAI',
    status_pending: 'Pendent d\'enviament a la Hisenda Foral',
    status_accepted: 'Rebut per la Hisenda Foral',
    status_accepted_with_errors: 'Acceptat per la Hisenda Foral amb errors',
    status_rejected: 'Rebutjat per la Hisenda Foral',
    status_error: 'No s\'ha pogut enviar a la Hisenda Foral',
    status_voided: 'Anul·lació rebuda per la Hisenda Foral',
    status_none: 'Sense fitxer TicketBAI',
    csv: 'Justificant',
    huella: 'Identificatiu TBAI',
    environment_test: 'Entorn de proves',
    environment_production: 'Producció',
    register: 'Genera i envia el fitxer TicketBAI',
    verifyLink: 'Comprova a la Hisenda Foral (QR)',
    registeredOk: 'Enviat a la Hisenda Foral.',
  },
  vf: {
    title: 'Verifactu',
    settingsTitle: 'Verifactu (AEAT)',
    intro: 'Cada factura i tiquet que emets genera un registre de facturació amb empremta encadenada i codi QR, i s\'envia a l\'Agència Tributària amb el teu certificat digital. Obligatori per al programari de facturació (RD 1007/2023).',
    certTitle: 'Certificat digital',
    certHint: 'Certificat de representant o de persona física (FNMT, Camerfirma…) en format .p12 / .pfx. Es desa xifrat i només el fa servir el servidor per signar la connexió amb l\'AEAT.',
    certFile: 'Fitxer .p12 / .pfx',
    certPassword: 'Contrasenya del certificat',
    certUpload: 'Desar certificat',
    certUploading: 'Comprovant…',
    certStored: 'Certificat desat',
    certHolder: 'Titular',
    certNif: 'NIF del certificat',
    certValidTo: 'Vàlid fins',
    certDaysLeft: '{{days}} dies',
    certExpired: 'Caducat',
    certReplace: 'Substituir certificat',
    certDelete: 'Treure certificat',
    certDeleteConfirm: 'Treure el certificat? Verifactu quedarà desactivat fins que en pugis un altre.',
    certNifMismatch: 'El NIF del certificat ({{cert}}) no coincideix amb el NIF de la teva empresa ({{issuer}}). L\'AEAT només accepta registres del titular o del seu representant.',
    certOk: 'Certificat desat: {{subject}}',
    testCert: 'Provar la connexió amb l\'AEAT',
    testCertOk: 'Connexió correcta amb {{host}} ({{protocol}}).',
    testCertOkNif: 'L\'AEAT reconeix el certificat i l\'accepta per al NIF {{nif}} ({{host}}).',
    testCertFail: 'L\'AEAT no ha acceptat la connexió: {{message}}',
    testSubmission: 'Enviar factura de prova (pre-producció)',
    testSubmissionHint: 'Envia un registre d\'exemple a l\'entorn de proves de l\'AEAT. No es desa ni afecta les teves factures.',
    testSubmissionOk: 'L\'AEAT ha acceptat la factura de prova {{number}}{{csv}}.',
    testSubmissionFail: 'L\'AEAT no ha acceptat la prova: {{message}}',
    modeTitle: 'Estat',
    mode_off: 'Desactivat',
    mode_offHint: 'Les factures s\'emeten com fins ara, sense registre ni QR.',
    mode_test: 'Proves (pre-producció AEAT)',
    mode_testHint: 'Els registres s\'envien a l\'entorn de proves de l\'AEAT. Serveix per comprovar el certificat i les dades sense efectes fiscals.',
    mode_production: 'Producció',
    mode_productionHint: 'Cada factura i tiquet emès es registra a l\'AEAT. Els documents porten el QR tributari i la llegenda VERI*FACTU.',
    modeSaved: 'Configuració desada.',
    softwareNifMissing: 'El servidor encara no té configurat el NIF del productor del programari (VERIFACTU_DEV_NIF); el mode Producció s\'activarà quan ho estigui.',
    counts: 'Registres: {{total}} · acceptats {{accepted}} · pendents {{pending}} · rebutjats {{rejected}}',
    since: 'Actiu des de {{date}}',
    needsMigration: 'Verifactu s\'activa en aplicar backend/verifactu.sql a la base de dades.',
    ownerOnly: 'Només el titular del compte pot canviar aquesta configuració.',
    error: 'Verifactu: {{message}}',
    panelTitle: 'Verifactu',
    status_pending: 'Pendent d\'enviament a l\'AEAT',
    status_accepted: 'Registrat a l\'AEAT',
    status_accepted_with_errors: 'Registrat a l\'AEAT amb avisos',
    status_rejected: 'Rebutjat per l\'AEAT',
    status_error: 'No s\'ha pogut enviar a l\'AEAT',
    status_voided: 'Anul·lació registrada a l\'AEAT',
    status_none: 'Sense registre Verifactu',
    csv: 'CSV',
    huella: 'Empremta',
    environment_test: 'Entorn de proves',
    environment_production: 'Producció',
    retry: 'Reintentar enviament',
    resend: 'Corregir i reenviar',
    retrying: 'Enviant…',
    register: 'Registrar a l\'AEAT',
    verifyLink: 'Comprovar a la seu de l\'AEAT',
    records: 'Registres ({{count}})',
    record_alta: 'Alta',
    record_anulacion: 'Anul·lació',
    downloadXml: 'Descarregar XML',
    errorDetail: 'Error {{code}}: {{description}}',
    registeredOk: 'Registre Verifactu enviat a l\'AEAT.',
    registerWarning: 'La factura s\'ha emès, però el registre Verifactu encara no s\'ha pogut enviar: {{message}} Podràs reintentar-ho des de la factura.',
    exemptionRequired: 'Amb Verifactu actiu, les línies al 0 % necessiten un motiu d\'exempció (Opcions avançades).',
    void: 'Anul·lar factura',
    voidHint: 'Anul·la una factura emesa per error. Deixa de comptar en totals i informes i, si estava registrada, s\'envia l\'anul·lació a l\'AEAT. Si el client ja la té, emet una rectificativa.',
    voidPrompt: 'Motiu de l\'anul·lació (obligatori):',
    voidConfirm: 'Anul·lar la factura {{number}}? Aquesta acció no es pot desfer.',
    voiding: 'Anul·lant…',
    voided: 'Anul·lada',
    voidedOn: 'Anul·lada el {{date}}',
    voidReason: 'Motiu: {{reason}}',
    voidError: 'No s\'ha pogut anul·lar: {{message}}',
    regimeLabel: 'Clau de règim (Verifactu)',
    regimeHint: 'Règim d\'IVA de l\'operació. Règim general (01) per a gairebé tot; 18 només amb recàrrec d\'equivalència.',
    regimeDefault: 'General (01)',
    regime_01: 'Operació de règim general',
    regime_02: 'Exportació',
    regime_03: 'Béns usats, art, antiguitats (REBU)',
    regime_04: 'Or d\'inversió',
    regime_05: 'Agències de viatges',
    regime_06: 'Grup d\'entitats (nivell avançat)',
    regime_07: 'Criteri de caixa',
    regime_08: 'Operacions subjectes a IPSI / IGIC',
    regime_09: 'Agències de viatges que intervenen en nom d\'altri',
    regime_10: 'Cobraments per compte de tercers',
    regime_11: 'Arrendament de local de negoci',
    regime_14: 'Factura amb IVA pendent de meritació (obra pública)',
    regime_15: 'Factura amb IVA pendent de meritació (tracte successiu)',
    regime_17: 'Finestreta única (OSS / IOSS)',
    regime_18: 'Recàrrec d\'equivalència',
    regime_19: 'Activitats REAGYP',
    regime_20: 'Règim simplificat',
    causeTitle: 'Motiu de la rectificativa',
    causeHint: 'La factura rectificativa ha d\'indicar per què s\'emet (art. 80 LIVA). Es registra a l\'AEAT amb aquest codi.',
    cause_R1: 'R1 · Error fonamentat en dret i art. 80.U, Dos i Sis LIVA',
    cause_R2: 'R2 · Concurs de creditors (art. 80.Tres)',
    cause_R3: 'R3 · Crèdits incobrables (art. 80.Quatre)',
    cause_R4: 'R4 · Resta de causes',
    cause_R5: 'R5 · Rectificativa de factures simplificades',
    typeTitle: 'Tipus',
    type_I: 'Per diferències (la rectificativa recull l\'import que es corregeix)',
    type_S: 'Per substitució (substitueix íntegrament la factura original)',
    creditNoteTitle: 'Emetre factura rectificativa',
    creditNoteBody: 'Es crearà una rectificativa negativa per l\'import total de {{number}} ({{amount}}) i la factura quedarà anul·lada per ella.',
    confirm: 'Emetre rectificativa',
    cancel: 'Cancel·lar',
  },
  docstatus: { Void: 'Anul·lada' },
  docHistory: {
    verifactu_pending: 'Registre Verifactu ({{kind}}) generat, pendent d\'enviament',
    verifactu_accepted: 'Registre Verifactu ({{kind}}) acceptat per l\'AEAT{{csv}}',
    verifactu_accepted_with_errors: 'Registre Verifactu ({{kind}}) acceptat amb avisos{{csv}}',
    verifactu_rejected: 'Registre Verifactu ({{kind}}) rebutjat per l\'AEAT: {{error}}',
    verifactu_error: 'Registre Verifactu ({{kind}}) no enviat: {{error}}',
    verifactu_kind_alta: 'alta',
    verifactu_kind_anulacion: 'anul·lació',
    voided: 'Factura anul·lada: {{reason}}',
  },
  doctpl: {
    vfLegend: 'VERI*FACTU',
    tbaiLegend: 'TicketBAI',
    vfScan: 'Factura verificable a la seu electrònica de l\'AEAT',
    vfVoid: 'ANUL·LADA',
  },
  subdash: { f: { verifactuAeat: 'Verifactu: registres amb empremta, QR i enviament a l\'AEAT.' } },
};

for (const [lng, res] of [['es', es], ['en', en], ['ca', ca]]) {
  i18n.addResourceBundle(lng, 'translation', res, true, false);
}

export { es, en, ca };
